import { playersAbove } from "../document/band.ts";
import { applyCommand, type Command } from "../document/commands.ts";
import { EMPTY_TILE, packedToTileRef, tileFlipsOf, tileRefToPacked, unpackTile, withoutTileFlips } from "../document/packed-tile.ts";
import type { MapDocument, TerrainLayer, TileLayer, TileRef, TilesetRef, WorldDocument } from "../document/types.ts";
import { buildCornerIndex } from "../terrain/resolve.ts";
import type { TerrainDefinition } from "../terrain/types.ts";
import { bandAfterMove } from "./layer-move.ts";
import { addLayerCommand, newLayer, setLayerTerrainCommand } from "./panel-commands.ts";

/**
 * Replace in selection, the pure part (ADR-097): what an area holds on the layers in scope, and the one
 * command that swaps the chosen tiles and replaces the chosen terrain.
 *
 * Tiles are swapped cell for cell and each cell keeps its own mirroring.
 *
 * A terrain layer holds one entry, a terrain and a colour (ADR-038 decision 3), and its tiles are derived
 * from its corners, so terrain is replaced by entry and the autotiler resolves it:
 *
 * - When the area takes every corner the layer paints, the layer is kept and retargeted: it takes the new
 *   terrain, every painted corner takes the new colour one for one, and a layer that follows its terrain's
 *   name takes the new name. No corner is set or cleared, so every cell keeps the number of set corners it
 *   had: cells that drew an interior tile draw the new terrain's interior, and the transition runs exactly
 *   where the old one ran.
 * - When the area cuts through the paint, the corners inside the area's corner rectangle are painted on a
 *   layer of the new entry, an editable one that already paints it or else a new one right above the source,
 *   and cleared on the source, except the corners on the rectangle's edge where the source's paint carries on
 *   outside it: those stay set on both layers. Along such a cut the two layers therefore overlap by the one
 *   ring of cells where a corner terrain draws its edge, so the upper layer's edge is drawn over the lower
 *   layer's interior and never beside the lower layer's own edge, and no bare ground shows through.
 *
 * The result may be placed below or above a named layer in the same command, joining that layer's group.
 * The command is a `compound` of commands that already exist (`fillCells`, `setLayerTerrain`, `addLayer`,
 * `moveLayer`, `setLayerGroup`), each built against the document the one before it left, so it inverts
 * exactly and needs no command kind of its own.
 */

/**
 * The area and the layers a replace reads: the tile selection's rectangle, both corners inclusive as the
 * selection holds them, or null for the whole map; and the layers in scope, of which only tile and terrain
 * layers are read. On a terrain layer the area is the rectangle's corners, columns `x0` to `x1 + 1` and rows
 * `y0` to `y1 + 1`.
 */
export interface ReplaceScope {
  readonly rect: { readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number } | null;
  readonly layerIds: readonly string[];
}

/**
 * A tile the area holds, counted in cells over every layer in scope, its mirrored copies with it.
 */
export interface ReplaceTileSource {
  readonly kind: "tile";
  readonly tileset: string;
  readonly tile: number;
  readonly count: number;
}

/**
 * A terrain layer that paints in the area: the entry it paints, its terrain and the colour its corners hold,
 * counted in corners.
 */
export interface ReplaceTerrainSource {
  readonly kind: "terrain";
  readonly layerId: string;
  readonly terrain: string;
  readonly colour: number;
  readonly count: number;
}

/**
 * One line of the "from" list.
 */
export type ReplaceSource = ReplaceTileSource | ReplaceTerrainSource;

/**
 * A terrain entry to replace with: the terrain as the editor uses it, one of its colours, and the name a
 * layer that follows its terrain's name takes for it.
 */
export interface ReplaceTerrainEntry {
  readonly terrain: TerrainDefinition;
  readonly colour: number;
  readonly name: string;
}

/**
 * Where the layer holding replaced terrain ends up in the draw order: where it is (a new layer right above
 * its source), or right below or above a named layer, in that layer's group.
 */
export type ReplacePlacement = { readonly kind: "keep" } | { readonly kind: "below" | "above"; readonly layerId: string };

/**
 * What to replace with what: each tile with another, whose own mirroring is ignored; each terrain layer's
 * paint in the area with an entry; and where the terrain's result is placed.
 */
export interface ReplacePicks {
  readonly tiles: readonly { readonly from: { readonly tileset: string; readonly tile: number }; readonly to: TileRef }[];
  readonly terrain: readonly { readonly from: string; readonly to: ReplaceTerrainEntry }[];
  readonly placement: ReplacePlacement;
}

/**
 * What a plan needs from the editor: the colour an unpainted terrain layer is set to paint, whether the tools
 * may edit a layer (a locked or hidden one is never chosen to take terrain), and a fresh layer id.
 */
export interface ReplaceEnv {
  readonly colourOf: (layerId: string) => number;
  readonly usable: (layerId: string) => boolean;
  readonly newLayerId: () => string;
}

/**
 * A replace ready to run: the command; the tile cells it changes; the corners whose terrain it replaces; the
 * layers it writes, in the map's order after it; the layers it adds; the cells of the replaced terrain's
 * layers whose corners match no tile of their terrain afterwards; and, when a rectangle cut through a layer's
 * paint, whether every layer that took a cut ends above the layer it was cut from in the order the command
 * leaves (null with no cut).
 */
export interface ReplacePlan {
  readonly command: Command;
  readonly cells: number;
  readonly corners: number;
  readonly layers: readonly string[];
  readonly made: readonly string[];
  readonly unresolved: number;
  readonly cutAbove: boolean | null;
}

const MAX_BYTE = 255;
const CORNER_SHIFT = 8;

/**
 * The bounds of an area in a grid: the rectangle clipped to the grid, widened by one column and row for a
 * corner grid, or the whole grid.
 *
 * @param rect The rectangle, or null for the whole grid.
 * @param width The grid's width.
 * @param height The grid's height.
 * @param corners True for a terrain layer's corner grid, whose width and height are one more than the map's.
 * @returns The first and last column and row, inclusive; empty when the last is before the first.
 */
function boundsOf(rect: ReplaceScope["rect"], width: number, height: number, corners: boolean): { readonly x0: number; readonly y0: number; readonly x1: number; readonly y1: number } {
  if (rect === null) {
    return { x0: 0, y0: 0, x1: width - 1, y1: height - 1 };
  }
  const extra = corners ? 1 : 0;
  return { x0: Math.max(0, rect.x0), y0: Math.max(0, rect.y0), x1: Math.min(width - 1, rect.x1 + extra), y1: Math.min(height - 1, rect.y1 + extra) };
}

/**
 * The layers in scope that a replace reads, in the map's order.
 *
 * @param map The map.
 * @param layerIds The layers in scope.
 * @returns The tile and terrain layers among them.
 */
function scopedLayers(map: MapDocument, layerIds: readonly string[]): (TileLayer | TerrainLayer)[] {
  const wanted = new Set(layerIds);
  return map.layers.filter((layer): layer is TileLayer | TerrainLayer => wanted.has(layer.id) && (layer.kind === "tile" || layer.kind === "terrain"));
}

/**
 * The colour a terrain layer's corners hold: the highest, as the painter reads it.
 *
 * @param layer The layer.
 * @returns The colour, or 0 for an unpainted layer.
 */
export function paintedColour(layer: TerrainLayer): number {
  let colour = 0;
  for (let i = 0; i < layer.corners.length; i += 1) {
    colour = Math.max(colour, layer.corners[i] as number);
  }
  return colour;
}

/**
 * Lists what an area holds on the layers in scope: each tile once per `(tileset, tile)` with its cells over
 * every tile layer in scope, the most used first and then in the tileset list's order, followed by each
 * terrain layer that paints a corner there, in the map's order.
 *
 * @param map The map.
 * @param scope The area and the layers.
 * @param tilesets The world's tilesets, which name a packed tile's tileset.
 * @returns The lines; none when the area holds nothing.
 */
export function scanReplace(map: MapDocument, scope: ReplaceScope, tilesets: readonly TilesetRef[]): ReplaceSource[] {
  const counts = new Map<number, number>();
  const terrain: ReplaceTerrainSource[] = [];
  for (const layer of scopedLayers(map, scope.layerIds)) {
    if (layer.kind === "tile") {
      const { x0, y0, x1, y1 } = boundsOf(scope.rect, map.width, map.height, false);
      for (let y = y0; y <= y1; y += 1) {
        for (let x = x0; x <= x1; x += 1) {
          const packed = layer.tiles[y * map.width + x] as number;
          if (packed !== EMPTY_TILE) {
            const base = withoutTileFlips(packed);
            counts.set(base, (counts.get(base) ?? 0) + 1);
          }
        }
      }
    } else {
      const stride = map.width + 1;
      const { x0, y0, x1, y1 } = boundsOf(scope.rect, stride, map.height + 1, true);
      let count = 0;
      for (let y = y0; y <= y1; y += 1) {
        for (let x = x0; x <= x1; x += 1) {
          count += layer.corners[y * stride + x] === 0 ? 0 : 1;
        }
      }
      if (count > 0) {
        terrain.push({ kind: "terrain", layerId: layer.id, terrain: layer.terrain, colour: paintedColour(layer), count });
      }
    }
  }
  const tiles = [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0] - b[0])
    .map(([packed, count]): ReplaceTileSource => {
      const { tilesetIndex, tile } = unpackTile(packed);
      return { kind: "tile", tileset: (tilesets[tilesetIndex] as TilesetRef).id, tile, count };
    });
  return [...tiles, ...terrain];
}

/**
 * The cells of a terrain layer whose corners match no selectable tile of its terrain: the cells the resolver
 * would leave empty with a warning.
 *
 * @param terrain The layer's terrain, a corner set.
 * @param corners The layer's corners.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @returns How many cells with a set corner have no tile, and each such corner configuration (top left, top
 *   right, bottom left, bottom right) with its cells.
 * @throws Error when the terrain is not a corner set.
 */
export function unresolvedTerrainCells(terrain: TerrainDefinition, corners: Readonly<Uint8Array>, width: number, height: number): { readonly cells: number; readonly configurations: readonly { readonly corners: readonly [number, number, number, number]; readonly cells: number }[] } {
  const index = buildCornerIndex(terrain);
  const stride = width + 1;
  const missing = new Map<number, number>();
  let cells = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const at = y * stride + x;
      const key = (((corners[at] as number) << (3 * CORNER_SHIFT)) | ((corners[at + 1] as number) << (2 * CORNER_SHIFT)) | ((corners[at + stride] as number) << CORNER_SHIFT) | (corners[at + stride + 1] as number)) >>> 0;
      if (key !== 0 && !index.byKey.has(key)) {
        missing.set(key, (missing.get(key) ?? 0) + 1);
        cells += 1;
      }
    }
  }
  const configurations = [...missing.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0]).map(([key, count]) => ({ corners: [key >>> (3 * CORNER_SHIFT), (key >>> (2 * CORNER_SHIFT)) & MAX_BYTE, (key >>> CORNER_SHIFT) & MAX_BYTE, key & MAX_BYTE] as const, cells: count }));
  return { cells, configurations };
}

/**
 * The `fillCells` commands that swap the picked tiles on one tile layer: one for each packed value held
 * there, so every cell of a command shares its value before and after and keeps its mirroring.
 *
 * @param map The map.
 * @param layer The tile layer.
 * @param rect The area.
 * @param swaps The packed tile to write for each packed tile replaced, neither carrying a mirror.
 * @param tilesets The world's tilesets.
 * @returns The commands and the cells they change.
 */
function tileFills(map: MapDocument, layer: TileLayer, rect: ReplaceScope["rect"], swaps: ReadonlyMap<number, number>, tilesets: readonly TilesetRef[]): { readonly commands: Command[]; readonly cells: number } {
  const groups = new Map<number, number[]>();
  const { x0, y0, x1, y1 } = boundsOf(rect, map.width, map.height, false);
  let cells = 0;
  for (let y = y0; y <= y1; y += 1) {
    for (let x = x0; x <= x1; x += 1) {
      const index = y * map.width + x;
      const packed = layer.tiles[index] as number;
      if (packed === EMPTY_TILE || !swaps.has(withoutTileFlips(packed))) {
        continue;
      }
      const group = groups.get(packed);
      if (group === undefined) {
        groups.set(packed, [index]);
      } else {
        group.push(index);
      }
      cells += 1;
    }
  }
  const commands = [...groups.entries()].map(([packed, indices]): Command => {
    const target = packedToTileRef(swaps.get(withoutTileFlips(packed)) as number, tilesets) as TileRef;
    const flips = tileFlipsOf(packed);
    const after: TileRef = { tileset: target.tileset, tile: target.tile, ...(flips.flipX ? { flipX: true } : {}), ...(flips.flipY ? { flipY: true } : {}) };
    return { kind: "fillCells", mapId: map.id, layerId: layer.id, indices: Uint32Array.from(indices), before: packedToTileRef(packed, tilesets), after };
  });
  return { commands, cells };
}

/**
 * The `fillCells` commands that write one value over marked corners of a terrain layer, one for each value
 * the corners hold now, leaving out the corners that already hold it.
 *
 * @param map The map.
 * @param layer The terrain layer.
 * @param marked One byte a corner, non-zero where the value is written.
 * @param value The colour to write, 0 to clear.
 * @returns The commands.
 */
export function cornerFills(map: MapDocument, layer: TerrainLayer, marked: Readonly<Uint8Array>, value: number): Command[] {
  const groups = new Map<number, number[]>();
  for (let index = 0; index < marked.length; index += 1) {
    const held = layer.corners[index] as number;
    if (marked[index] === 0 || held === value) {
      continue;
    }
    const group = groups.get(held);
    if (group === undefined) {
      groups.set(held, [index]);
    } else {
      group.push(index);
    }
  }
  return [...groups.entries()].map(([held, indices]): Command => ({ kind: "fillCells", mapId: map.id, layerId: layer.id, indices: Uint32Array.from(indices), before: held, after: value }));
}

/**
 * A plan while it is built: the document as the commands so far leave it, and what they did, among it each
 * cut with its source layer and the layer that took it.
 */
interface Draft {
  world: WorldDocument;
  readonly mapId: string;
  readonly commands: Command[];
  readonly written: Set<string>;
  readonly made: string[];
  readonly results: Map<string, TerrainDefinition>;
  readonly cuts: { readonly from: string; readonly to: string }[];
  cells: number;
  corners: number;
}

/**
 * The map a draft is planned on, as its commands so far leave it.
 *
 * @param draft The draft.
 * @returns The map.
 * @throws Error when the world has no such map.
 */
function mapOf(draft: Draft): MapDocument {
  const map = draft.world.maps.find((m) => m.id === draft.mapId);
  if (map === undefined) {
    throw new Error(`map ${draft.mapId} does not exist`);
  }
  return map;
}

/**
 * A terrain layer of a draft's map by id.
 *
 * @param draft The draft.
 * @param layerId The layer.
 * @returns The layer.
 * @throws Error when the map has no terrain layer of that id.
 */
function terrainLayerOf(draft: Draft, layerId: string): TerrainLayer {
  const layer = mapOf(draft).layers.find((l) => l.id === layerId);
  if (layer?.kind !== "terrain") {
    throw new Error(`layer ${layerId} is not a terrain layer of the map`);
  }
  return layer;
}

/**
 * Adds commands to a draft, each applied to the draft's document so the next is built against what it left.
 *
 * @param draft The draft.
 * @param commands The commands, in order.
 * @param layerId The layer they write.
 * @throws Error when the document refuses one.
 */
function push(draft: Draft, commands: readonly Command[], layerId: string): void {
  for (const command of commands) {
    draft.world = applyCommand(draft.world, command);
    draft.commands.push(command);
    draft.written.add(layerId);
  }
}

/**
 * The corners of the area a terrain layer paints, and whether they are every corner it paints.
 *
 * @param map The map.
 * @param layer The layer.
 * @param rect The area.
 * @returns One byte a corner, non-zero where the layer paints inside the area; how many; and whether none is
 *   painted outside it.
 */
function paintedInArea(map: MapDocument, layer: TerrainLayer, rect: ReplaceScope["rect"]): { readonly marked: Uint8Array; readonly count: number; readonly whole: boolean } {
  const stride = map.width + 1;
  const marked = new Uint8Array(layer.corners.length);
  const { x0, y0, x1, y1 } = boundsOf(rect, stride, map.height + 1, true);
  let count = 0;
  for (let y = y0; y <= y1; y += 1) {
    for (let x = x0; x <= x1; x += 1) {
      const index = y * stride + x;
      if (layer.corners[index] !== 0) {
        marked[index] = 1;
        count += 1;
      }
    }
  }
  let painted = 0;
  for (let i = 0; i < layer.corners.length; i += 1) {
    painted += layer.corners[i] === 0 ? 0 : 1;
  }
  return { marked, count, whole: painted === count };
}

/**
 * The corners a cut source layer gives up: the marked corners less those on the area's edge next to which
 * the layer paints a corner outside the area, which stay set so the layer's interior runs up to the cut.
 *
 * @param map The map.
 * @param layer The source layer.
 * @param rect The area, a rectangle.
 * @param marked The corners of the area the layer paints.
 * @returns One byte a corner, non-zero where the source is cleared.
 */
function clearedOnCut(map: MapDocument, layer: TerrainLayer, rect: NonNullable<ReplaceScope["rect"]>, marked: Readonly<Uint8Array>): Uint8Array {
  const stride = map.width + 1;
  const rows = map.height + 1;
  const { x0, y0, x1, y1 } = boundsOf(rect, stride, rows, true);
  const cleared = Uint8Array.from(marked);
  /**
   * Whether the layer paints a corner outside the area next to a corner.
   *
   * @param x The corner's column.
   * @param y The corner's row.
   * @returns True when one of its eight neighbours lies outside the area and is painted.
   */
  const carriesOn = (x: number, y: number): boolean => {
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        const nx = x + dx;
        const ny = y + dy;
        const outside = nx < x0 || nx > x1 || ny < y0 || ny > y1;
        if (outside && nx >= 0 && ny >= 0 && nx < stride && ny < rows && layer.corners[ny * stride + nx] !== 0) {
          return true;
        }
      }
    }
    return false;
  };
  for (let y = y0; y <= y1; y += 1) {
    for (let x = x0; x <= x1; x += 1) {
      const edge = x === x0 || x === x1 || y === y0 || y === y1;
      if (edge && marked[y * stride + x] !== 0 && carriesOn(x, y)) {
        cleared[y * stride + x] = 0;
      }
    }
  }
  return cleared;
}

/**
 * Retargets a layer whose every painted corner is replaced: its terrain, its corners' colour one for one and,
 * for a layer that follows its terrain's name, its name. The corners are cleared before the terrain changes
 * and painted after it when the colour differs, so no step leaves a colour its terrain lacks.
 *
 * @param draft The draft.
 * @param layer The layer.
 * @param marked The corners it paints.
 * @param to The entry it takes.
 */
function retarget(draft: Draft, layer: TerrainLayer, marked: Readonly<Uint8Array>, to: ReplaceTerrainEntry): void {
  const recolour = paintedColour(layer) !== to.colour;
  if (recolour) {
    push(draft, cornerFills(mapOf(draft), layer, marked, 0), layer.id);
  }
  const name = layer.autoName === true && layer.name !== to.name ? to.name : undefined;
  if (layer.terrain !== to.terrain.id || name !== undefined) {
    push(draft, [setLayerTerrainCommand(mapOf(draft), layer.id, to.terrain, layer.variant, name)], layer.id);
  }
  if (recolour) {
    push(draft, cornerFills(mapOf(draft), terrainLayerOf(draft, layer.id), marked, to.colour), layer.id);
  }
}

/**
 * Moves the paint a cut takes from a source layer to a layer of the new entry: the topmost editable layer
 * that already paints it and is not itself being replaced, or a new one right above the source in the
 * source's group, named after the entry and following it.
 *
 * @param draft The draft.
 * @param layer The source layer.
 * @param rect The area, a rectangle.
 * @param marked The corners of the area the source paints.
 * @param to The entry.
 * @param sources The layers being replaced, none of which takes terrain.
 * @param env What the plan needs from the editor.
 * @returns The id of the layer that took the paint.
 */
function moveCut(draft: Draft, layer: TerrainLayer, rect: NonNullable<ReplaceScope["rect"]>, marked: Readonly<Uint8Array>, to: ReplaceTerrainEntry, sources: ReadonlySet<string>, env: ReplaceEnv): string {
  const map = mapOf(draft);
  /**
   * Whether a terrain layer paints the entry's colour: the colour its corners hold, or for an unpainted
   * layer the colour the editor has it set to paint.
   *
   * @param candidate The layer.
   * @returns True when it does.
   */
  const paints = (candidate: TerrainLayer): boolean => (paintedColour(candidate) || env.colourOf(candidate.id)) === to.colour;
  const existing = [...map.layers].reverse().find((l): l is TerrainLayer => l.kind === "terrain" && l.terrain === to.terrain.id && !sources.has(l.id) && (draft.made.includes(l.id) || env.usable(l.id)) && paints(l));
  let targetId = existing?.id;
  if (targetId === undefined) {
    targetId = env.newLayerId();
    const made = { ...(newLayer(map, "terrain", targetId, to.name, to.terrain) as TerrainLayer), autoName: true, ...(layer.group === undefined ? {} : { group: layer.group }) };
    push(draft, [addLayerCommand(map, made, undefined, map.layers.findIndex((l) => l.id === layer.id) + 1)], targetId);
    draft.made.push(targetId);
  }
  push(draft, cornerFills(mapOf(draft), terrainLayerOf(draft, targetId), marked, to.colour), targetId);
  push(draft, cornerFills(mapOf(draft), terrainLayerOf(draft, layer.id), clearedOnCut(map, layer, rect, marked), 0), layer.id);
  return targetId;
}

/**
 * Places a layer right below or above another, in that layer's group and on its side of the Players band. The
 * move carries the band before and after, as every layer move the store builds does, so the command and the
 * operations live editing sends for it leave the same band; on a map that pinned none it pins it where it
 * stood.
 *
 * @param draft The draft.
 * @param layerId The layer placed.
 * @param placement Where it goes.
 * @throws Error when the map does not hold the layer it is placed against.
 */
function place(draft: Draft, layerId: string, placement: Exclude<ReplacePlacement, { readonly kind: "keep" }>): void {
  if (placement.layerId === layerId) {
    return;
  }
  const map = mapOf(draft);
  const from = map.layers.findIndex((l) => l.id === layerId);
  const beside = map.layers.findIndex((l) => l.id === placement.layerId);
  if (beside < 0) {
    throw new Error(`layer ${placement.layerId} does not exist`);
  }
  const band = playersAbove(map);
  const shifted = beside - (from < beside ? 1 : 0);
  const to = placement.kind === "below" ? shifted : shifted + 1;
  const after = bandAfterMove(band, from, to, beside < band);
  if (after !== null) {
    push(draft, [{ kind: "moveLayer", mapId: map.id, from, to, layerId, playersAbove: { before: band, after } }], layerId);
  }
  const group = map.layers[beside]?.group;
  const held = map.layers[from]?.group;
  if (group !== held) {
    push(draft, [{ kind: "setLayerGroup", mapId: map.id, layerId, before: held, after: group }], layerId);
  }
}

/**
 * Plans a replace as one command that inverts exactly. Every tile cell is read as the map holds it, so
 * swapping two tiles with each other in one replace swaps them. Only the layers in scope are read as
 * sources; the layer that takes a cut's terrain may lie outside them.
 *
 * @param world The world.
 * @param mapId The map replaced on.
 * @param scope The area and the layers replaced on.
 * @param picks What to replace with what, and where the terrain's result is placed.
 * @param env What the plan needs from the editor.
 * @returns The plan, or null when nothing would change.
 * @throws Error when the map is missing, a tile's tileset is not in the world, a terrain entry's terrain is not
 *   a corner set or lacks the colour, or the layer the result is placed against is not in the map.
 */
export function planReplace(world: WorldDocument, mapId: string, scope: ReplaceScope, picks: ReplacePicks, env: ReplaceEnv): ReplacePlan | null {
  const draft: Draft = { world, mapId, commands: [], written: new Set(), made: [], results: new Map(), cuts: [], cells: 0, corners: 0 };
  const map = mapOf(draft);
  for (const pick of picks.terrain) {
    if (pick.to.terrain.type !== "corner" || !pick.to.terrain.colors.some((c) => c.index === pick.to.colour)) {
      throw new Error(`terrain ${pick.to.terrain.id} is not a corner set with colour ${String(pick.to.colour)}`);
    }
  }
  const swaps = new Map<number, number>();
  for (const pick of picks.tiles) {
    const from = tileRefToPacked({ tileset: pick.from.tileset, tile: pick.from.tile }, world.tilesets);
    const to = tileRefToPacked({ tileset: pick.to.tileset, tile: pick.to.tile }, world.tilesets);
    if (from !== to) {
      swaps.set(from, to);
    }
  }
  const moves = new Map(picks.terrain.map((pick) => [pick.from, pick.to]));
  const sources = new Set(moves.keys());
  for (const scoped of scopedLayers(map, scope.layerIds)) {
    if (scoped.kind === "tile") {
      if (swaps.size > 0) {
        const fills = tileFills(map, scoped, scope.rect, swaps, world.tilesets);
        draft.cells += fills.cells;
        push(draft, fills.commands, scoped.id);
      }
      continue;
    }
    const to = moves.get(scoped.id);
    const layer = terrainLayerOf(draft, scoped.id);
    if (to === undefined || (layer.terrain === to.terrain.id && paintedColour(layer) === to.colour)) {
      continue;
    }
    const area = paintedInArea(mapOf(draft), layer, scope.rect);
    if (area.count === 0) {
      continue;
    }
    draft.corners += area.count;
    if (area.whole || scope.rect === null) {
      retarget(draft, layer, area.marked, to);
      draft.results.set(layer.id, to.terrain);
    } else {
      const taker = moveCut(draft, layer, scope.rect, area.marked, to, sources, env);
      draft.results.set(taker, to.terrain);
      draft.cuts.push({ from: layer.id, to: taker });
    }
  }
  if (draft.commands.length === 0) {
    return null;
  }
  const placement = picks.placement;
  if (placement.kind !== "keep") {
    for (const layerId of draft.results.keys()) {
      place(draft, layerId, placement);
    }
  }
  const after = mapOf(draft);
  let unresolved = 0;
  for (const [layerId, terrain] of draft.results) {
    unresolved += unresolvedTerrainCells(terrain, terrainLayerOf(draft, layerId).corners, after.width, after.height).cells;
  }
  return {
    command: { kind: "compound", commands: draft.commands },
    cells: draft.cells,
    corners: draft.corners,
    layers: after.layers.filter((l) => draft.written.has(l.id)).map((l) => l.id),
    made: draft.made,
    unresolved,
    cutAbove: draft.cuts.length === 0 ? null : draft.cuts.every((cut) => after.layers.findIndex((l) => l.id === cut.to) > after.layers.findIndex((l) => l.id === cut.from)),
  };
}
